import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { DeleteDigitalSignatureModal } from "../../components/delete-digital-signature";
import { PageLayout } from "../../components/page-layout";
import { CardGridPagination } from "../../components/card-grid-pagination/CardGridPagination";
import { QdsStatusEnum } from "../../entities/IQdsRequest";
import { QdsSignatures } from "./QdsSignatures";
import { useQdsApprovals } from "./useQdsApprovals";

const APPROVALS_FILTER_KEY = "qds-approvals-status-filter";

export const QdsApprovalsScreen = () => {
  const {
    documents,
    count,
    onResend,
    loading,
    showDeleteModal,
    onStartDelete,
    closeDeleteModal,
    onDelete,
    deletingSignature,
  } = useQdsApprovals();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!location.search) {
      const saved = sessionStorage.getItem(APPROVALS_FILTER_KEY);
      if (saved) navigate({ search: saved }, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.search) {
      sessionStorage.setItem(APPROVALS_FILTER_KEY, location.search);
    } else {
      sessionStorage.removeItem(APPROVALS_FILTER_KEY);
    }
  }, [location.search]);

  const queryParams = new URLSearchParams(location.search);
  const currentStatus = queryParams.get("status");
  const showPending = !currentStatus || currentStatus === QdsStatusEnum.PENDING;
  const showApproved = !currentStatus || currentStatus === QdsStatusEnum.APPROVED;

  const handleFilterChange = (statusKey: QdsStatusEnum.PENDING | QdsStatusEnum.APPROVED, checked: boolean) => {
    const otherChecked = statusKey === QdsStatusEnum.PENDING ? showApproved : showPending;
    if (!checked && !otherChecked) return;

    const newParams = new URLSearchParams(location.search);
    newParams.delete("skip");

    if (checked && otherChecked) {
      newParams.delete("status");
    } else if (checked) {
      newParams.set("status", statusKey);
    } else {
      newParams.set("status", statusKey === QdsStatusEnum.PENDING ? QdsStatusEnum.APPROVED : QdsStatusEnum.PENDING);
    }

    navigate({ search: newParams.toString() });
  };

  return (
    <PageLayout title={t("DOCUMENTS.DOCUMENTS_FOR_QDS_APPROVAL")} loading={loading}>
      <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", margin: 0 }}>
          <input
            type="checkbox"
            checked={showPending}
            onChange={(e) => handleFilterChange(QdsStatusEnum.PENDING, e.target.checked)}
          />
          Čeka odobrenje
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", margin: 0 }}>
          <input
            type="checkbox"
            checked={showApproved}
            onChange={(e) => handleFilterChange(QdsStatusEnum.APPROVED, e.target.checked)}
          />
          Odobreno
        </label>
      </div>
      <hr style={{ marginTop: 12, marginBottom: 0 }} />
      <div style={{ paddingTop: 12 }}>
        <QdsSignatures documents={documents} onDelete={onStartDelete} onResend={onResend} mode="approvals" />
        <CardGridPagination totalItems={count} />
      </div>
      <DeleteDigitalSignatureModal
        show={showDeleteModal}
        onDelete={onDelete}
        onCancel={closeDeleteModal}
        isLoading={deletingSignature}
      />
    </PageLayout>
  );
};
